import type { URLSearchParamsInit} from 'react-router-dom';

import { useMemo } from 'react';
import { useNavigate, createSearchParams } from 'react-router-dom';

// Remix-run / react-router browser history stores this shape on session entries.
type HistoryStateWithIdx = { idx?: number };

function scrollToTopAfterNav() {
  requestAnimationFrame(() => {
    window.scrollTo(0, 0);
  });
}

// ----------------------------------------------------------------------

export function useRouter() {
  const navigate = useNavigate();

  const router = useMemo(
    () => ({
      back: () => {
        navigate(-1);
        // Use requestAnimationFrame to ensure scroll happens after navigation completes
        scrollToTopAfterNav();
      },
      /**
       * Goes back in the SPA session when there is a prior in-app history entry
       * (react-router tracks `history.state.idx`). Otherwise navigates to `fallbackHref`
       * so we never `navigate(-1)` off the app when the detail view was the first entry.
       */
      backOrFallback: (fallbackHref: string) => {
        const idx = (window.history.state as HistoryStateWithIdx | null)?.idx;
        if (typeof idx === 'number' && idx > 0) {
          navigate(-1);
        } else {
          navigate({ pathname: fallbackHref, search: '' });
        }
        scrollToTopAfterNav();
      },
      forward: () => navigate(1),
      refresh: () => navigate(0),
      push: (href: string, searchParams?: URLSearchParamsInit) => navigate({
        pathname: href,
        search: createSearchParams(searchParams).toString(),
      }),
      replace: (href: string) => navigate(href, { replace: true }),
    }),
    [navigate]
  );

  return router;
}
